import React, { useEffect, useRef, useState } from "react";

import { faChevronDown } from "@fortawesome/pro-solid-svg-icons/faChevronDown";
import { faChevronUp } from "@fortawesome/pro-solid-svg-icons/faChevronUp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button } from "@mapmycustomers/ui";

import { RegistryProps } from "@app/component/FieldGrid/utils/ComponentRegistry";
import Lexer from "@app/component/Lexer";
import MultilineText from "@app/component/typography/MultilineText";

import styles from "./LargeTextCustomField.module.scss";

const messages = defineMessages({
  showLess: {
    id: "fieldGrid.largeTextCustomField.showLess",
    defaultMessage: "Show less",
    description: "Button to show less text in large text custom field",
  },
  showMore: {
    id: "fieldGrid.largeTextCustomField.showMore",
    defaultMessage: "Show more",
    description: "Button to show more text in large text custom field",
  },
});

const LargeTextCustomField = ({ entity, field }: RegistryProps) => {
  const intl = useIntl();
  const [showAll, , , toggleShowAll] = useBoolean();
  const contentRef = useRef<HTMLDivElement>(null);

  const value = field.getValueFor(entity) as string;

  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const wrapperElement = contentRef.current;
    if (!wrapperElement) {
      return;
    }

    const listener = () => {
      const hasOverflow = wrapperElement.scrollHeight > wrapperElement.offsetHeight;
      setIsOverflowing(hasOverflow);
    };

    const observer = new ResizeObserver(listener);

    observer.observe(wrapperElement);

    listener();

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div className={styles.container}>
      <div className={cn(styles.value, { [styles.showAll]: showAll })} ref={contentRef}>
        <MultilineText wrapLinesWith={Lexer}>{value}</MultilineText>
      </div>
      {(isOverflowing || showAll) && (
        <Button
          onClick={toggleShowAll}
          rightIcon={<FontAwesomeIcon icon={showAll ? faChevronUp : faChevronDown} />}
          size="small"
          type="link"
        >
          {intl.formatMessage(showAll ? messages.showLess : messages.showMore)}
        </Button>
      )}
    </div>
  );
};

export default LargeTextCustomField;
